import type { JSX } from 'react';
import { PageHeader } from '@bookora/ui';

import { useAuth } from '@/auth/token-store';
import { useDocumentTitle } from '@/hooks/use-document-title';

import { SecurityPanel } from './security-panel';

/**
 * ════════════════════════════════════════════════════════════════════════════
 *  «امنیت حساب» IS ABOUT THE ACCOUNT, NOT ABOUT THE CLINIC
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Two-factor enrolment and «دستگاه‌های فعال» belong to the signed-in person.
 * They were only reachable as a tab inside `/settings`, and the whole section
 * is gated on `settings.view` — which neither `NURSE_PERMISSIONS` nor
 * `RECEPTIONIST_PERMISSIONS` holds (`packages/shared/src/permissions.ts`). So
 * two of the five shipped role presets could not enrol a second factor or kill
 * a stolen session, while the avatar menu went on showing them a «ورود
 * دومرحله‌ای» badge. The settings page even carried a fallback tab written for
 * exactly that user (`params.get('tab') ?? (canView ? 'clinic' : 'security')`),
 * which no caller could ever reach.
 *
 * The fix is not to widen `settings.view` — a منشی has no business in the
 * clinic's subscription — but to give the account screen its own route with no
 * clinic permission on it at all. `routes.tsx` mounts it un-gated and the
 * avatar menu links to it for everybody, which is also the right information
 * architecture: account-level settings hang off the account, not off the
 * clinic.
 *
 * The `?tab=security` tab inside `/settings` stays for owners and accountants,
 * so existing links and muscle memory keep working. Both render the same
 * `SecurityPanel`; there is one implementation.
 */
export default function AccountSecurityPage(): JSX.Element {
  const { activeClinic } = useAuth();
  useDocumentTitle('امنیت حساب', activeClinic?.clinicName);

  return (
    <>
      <PageHeader
        title="امنیت حساب"
        description="تأیید دومرحله‌ای، کدهای بازیابی و دستگاه‌هایی که با حساب شما وارد شده‌اند."
      />
      <SecurityPanel />
    </>
  );
}
